import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, XCircle, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, eur } from "@/lib/api";
import { EASE } from "@/components/motion";

export default function PaymentResult({ kind }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(kind === "cancel" ? "cancelled" : "checking");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (kind !== "success" || !sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          const bid = localStorage.getItem("hw_pending_booking");
          if (bid) {
            const b = await api.get(`/bookings/${bid}`);
            setBooking(b.data);
            localStorage.removeItem("hw_pending_booking");
          }
          return;
        }
      } catch (e) { /* retry */ }
      tries += 1;
      if (tries < 15) setTimeout(poll, 2500);
      else setState("timeout");
    };
    poll();
  }, [kind, sessionId]);

  return (
    <main className="bg-sand min-h-screen" data-testid="payment-result-page">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 sm:px-8 pt-36 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="bg-white rounded-3xl border border-forest/10 shadow-2xl shadow-forest/10 p-8 sm:p-12 text-center"
        >
          {state === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 className="mx-auto animate-spin text-forest" size={44} />
              <h1 className="mt-6 font-serif text-3xl font-semibold text-forest">Zahlung wird geprüft …</h1>
              <p className="mt-3 text-ink/60 text-sm">Einen Moment bitte, wir bestätigen Ihre Zahlung.</p>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="payment-success">
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}
                className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-forest text-sand">
                <Check size={30} />
              </motion.span>
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold text-forest">Bezahlt & bestätigt!</h1>
              <p className="mt-3 text-ink/60 text-sm sm:text-base">Ihre Buchung ist bezahlt und fest bestätigt. Wir freuen uns auf Sie in Dülmen!</p>
              {booking && (
                <div className="mt-8 rounded-2xl bg-cream/70 border border-forest/10 p-6 text-left text-sm space-y-2">
                  <p className="flex justify-between"><span className="text-ink/50">Buchungsnummer</span><strong className="font-mono" data-testid="paid-reference">{booking.reference}</strong></p>
                  <p className="flex justify-between"><span className="text-ink/50">Zimmer</span><strong>{booking.room_name}</strong></p>
                  <p className="flex justify-between"><span className="text-ink/50">Zeitraum</span><strong>{new Date(booking.check_in).toLocaleDateString("de-DE")} – {new Date(booking.check_out).toLocaleDateString("de-DE")}</strong></p>
                  <p className="flex justify-between pt-2 border-t border-forest/10"><span className="text-ink/50">Bezahlt</span><strong className="font-serif text-xl text-terra">{eur(booking.total_price)}</strong></p>
                </div>
              )}
            </div>
          )}
          {state === "timeout" && (
            <div data-testid="payment-timeout">
              <Loader2 className="mx-auto text-gold" size={44} />
              <h1 className="mt-6 font-serif text-3xl font-semibold text-forest">Prüfung dauert länger</h1>
              <p className="mt-3 text-ink/60 text-sm">Ihre Zahlung wird noch verarbeitet. Sie erhalten in Kürze eine Bestätigung per E-Mail.</p>
            </div>
          )}
          {state === "cancelled" && (
            <div data-testid="payment-cancelled">
              <XCircle className="mx-auto text-terra" size={44} />
              <h1 className="mt-6 font-serif text-3xl font-semibold text-forest">Zahlung abgebrochen</h1>
              <p className="mt-3 text-ink/60 text-sm">Kein Problem — Ihre Reservierung wurde nicht abgeschlossen. Sie können es jederzeit erneut versuchen oder per Überweisung zahlen.</p>
            </div>
          )}
          <Link to={state === "cancelled" ? "/buchen" : "/"} data-testid="payment-result-home-link"
            className="mt-8 inline-block rounded-full bg-forest text-sand px-8 py-3 text-sm font-semibold hover:bg-forest-dark transition-colors">
            {state === "cancelled" ? "Zurück zur Buchung" : "Zur Startseite"}
          </Link>
        </motion.div>
      </div>
      <Footer />
    </main>
  );
}
